// src/auth/pendingInvitation.js — the invitation token in flight, per TAB.
//
// An invitation link carries a single-use token but needs a SESSION: the accept
// endpoint requires a bearer, and the invited person is usually signed out (or
// signed in as somebody else) when they click it. So the token is parked here,
// the user is sent through /login, and login() resumes it.
//
// ⛔ PER TAB BY DESIGN (sessionStorage, not localStorage). A new tab does NOT
// inherit it — notably the tab a password-reset mail opens — so the login banner
// tells the user to REOPEN the invitation link after a reset. The link stays
// valid for 7 days, which makes reopening the honest instruction; leaking the
// token into every tab of the browser (localStorage) to save that one step is
// not a trade we make for a credential.
//
// ⛔ The value IS a credential — whoever holds it can join the workspace as the
// invited account. Never log it, never put it in a URL we build, never render it.
//
// ⚠️ Every access is wrapped: storage throws outright in some privacy modes. A
// failure degrades to "no pending invitation", which the accept page words as a
// missing token — never as a crash.

const KEY = 'pending_invitation';

// The backend mints 43 chars of `[A-Za-z0-9_-]` (url-safe base64, single use,
// 7 days). We accept the shape rather than the exact length so a backend that
// re-sizes its tokens does not break the page, but we refuse anything that is
// not a plain opaque handle: an implausible value means a mangled or hand-edited
// link, and the page says so WITHOUT spending a request on it.
const TOKEN_RE = /^[A-Za-z0-9_-]{1,256}$/;

export function isPlausibleInvitationToken(token) {
  return typeof token === 'string' && TOKEN_RE.test(token);
}

export function writePendingInvitation(token) {
  if (!isPlausibleInvitationToken(token)) return;
  try { window.sessionStorage.setItem(KEY, token); }
  catch { /* storage blocked — the page degrades to "no pending invitation" */ }
}

export function readPendingInvitation() {
  let value;
  try { value = window.sessionStorage.getItem(KEY); }
  catch { return null; }
  // An implausible stored value is treated as absent, so a corrupted entry can
  // never be handed to the API nor trap a later sign-in.
  return isPlausibleInvitationToken(value) ? value : null;
}

export function clearPendingInvitation() {
  try { window.sessionStorage.removeItem(KEY); }
  catch { /* nothing to clean up */ }
}

export function hasPendingInvitation() {
  return readPendingInvitation() !== null;
}
